import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import AddIcon from '@mui/icons-material/Add';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { LockOutlined as LockOutlinedIcon } from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import { useApi } from 'lib/api';
import {
  PROGRAMA_PRESTAMOS_NOMINA,
  puedeVerProgramaNominaPropio,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx, fpDataGridSx } from 'components/financepro';
import EstadoPrestamos from './EstadoPrestamos';

export { PROGRAMA_PRESTAMOS_NOMINA };

const btnBase = { textTransform: 'none', borderRadius: 1.8, fontWeight: 700, boxShadow: 'none' };
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: '#FFFFFF',
  border: `1px solid ${c}`,
  '&:hover': { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
  '&.Mui-disabled': { color: FP.color.textMuted, borderColor: FP.color.border },
});
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: '#FFFFFF',
  '&:hover': { bgcolor: hover, boxShadow: 'none', color: '#FFFFFF' },
  '&.Mui-disabled': { bgcolor: `${bg}66`, color: '#FFFFFF' },
});

const cardShellSx = {
  borderRadius: 2.5,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  border: `1px solid ${FP.color.border}`,
  overflow: 'hidden',
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

const fmtMoney = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(String(v || '').replace(/,/g, ''));
  if (Number.isNaN(n)) return '0.00';
  return n.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hoyISO = () => new Date().toISOString().slice(0, 10);

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: '100%', py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin cuotas pendientes
      </Typography>
    </Stack>
  );
}

const columnsCuotasPendientes = [
  { field: 'noprest', headerName: 'No. Prest', width: 90 },
  { field: 'numerodecuota', headerName: 'No. Cuota', width: 90 },
  {
    field: 'valorcuota',
    headerName: 'Valor',
    width: 120,
    align: 'right',
    headerAlign: 'right',
    valueFormatter: (value) => fmtMoney(value),
  },
  { field: 'nomina', headerName: 'Nómina', width: 100 },
];

const columnsCuotasNuevas = [
  { field: 'numerodecuota', headerName: 'No. Cuota', width: 100 },
  {
    field: 'valorcuota',
    headerName: 'Valor',
    flex: 1,
    align: 'right',
    headerAlign: 'right',
    valueFormatter: (value) => fmtMoney(value),
  },
];

const RegistroPrestamos = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { usuarioLog, roleDetalles, nombreNegocio } = controller;
  const { post } = useApi();

  const puedeLeer = useMemo(
    () => puedeVerProgramaNominaPropio(roleDetalles, PROGRAMA_PRESTAMOS_NOMINA),
    [roleDetalles],
  );
  const puedeOperar = Boolean(selectedCompany?.db_empresa && usuarioLog);

  const [empleados, setEmpleados] = useState([]);
  const [empleadoSel, setEmpleadoSel] = useState(null);
  const [nombres, setNombres] = useState('');
  const [fecha, setFecha] = useState(hoyISO());
  const [balanceActual, setBalanceActual] = useState('');
  const [cuotasPendientes, setCuotasPendientes] = useState('');
  const [prestacionesAcumuladas, setPrestacionesAcumuladas] = useState('');
  const [prestamoSolicitado, setPrestamoSolicitado] = useState('');
  const [motivos, setMotivos] = useState([]);
  const [motivoSel, setMotivoSel] = useState(null);
  const [cantidadCuotas, setCantidadCuotas] = useState('');
  const [cuotasDetalle, setCuotasDetalle] = useState([]);
  const [cuotasNuevas, setCuotasNuevas] = useState([]);
  const [prestamoIdAnterior, setPrestamoIdAnterior] = useState(null);

  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [empleadoCargado, setEmpleadoCargado] = useState(false);
  const [vistaEstado, setVistaEstado] = useState(false);

  const credencialesEmpresa = useCallback(
    () => ({ id_empresa: selectedCompany }),
    [selectedCompany],
  );

  useEffect(() => {
    if (!puedeLeer || !puedeOperar) return;
    (async () => {
      try {
        const { ok, data } = await post('/api/nomina_listarempleados', credencialesEmpresa());
        if (ok && Array.isArray(data.empleados)) setEmpleados(data.empleados);
      } catch { /* silencioso */ }
    })();
  }, [puedeLeer, puedeOperar, post, credencialesEmpresa]);

  const solicitadoMasPendiente = useMemo(() => {
    const bal = parseFloat(balanceActual) || 0;
    const sol = parseFloat(prestamoSolicitado) || 0;
    return bal + sol;
  }, [balanceActual, prestamoSolicitado]);

  useEffect(() => {
    const cuotas = parseInt(cantidadCuotas) || 0;
    if (cuotas <= 0 || solicitadoMasPendiente <= 0) {
      setCuotasNuevas([]);
      return;
    }
    const valor = solicitadoMasPendiente / cuotas;
    const nuevas = [];
    for (let i = 1; i <= cuotas; i++) {
      nuevas.push({ id: i, numerodecuota: i, valorcuota: Math.round(valor * 100) / 100 });
    }
    setCuotasNuevas(nuevas);
  }, [cantidadCuotas, solicitadoMasPendiente]);

  const buscarEmpleado = useCallback(
    async (emp) => {
      if (!emp) return;
      setCargando(true);
      try {
        const numemp = typeof emp === 'object' ? emp.numemp : emp;
        const { ok, data } = await post('/api/nomina_prestamo_buscarempleado', {
          ...credencialesEmpresa(),
          numemp: String(numemp),
        });
        if (!ok) throw new Error(data.Error || 'Error al buscar empleado');
        setEmpleadoSel(emp);
        setNombres(data.empleado?.nombres || '');
        setBalanceActual(data.balance_actual ? String(data.balance_actual) : '');
        setCuotasPendientes(data.cuotas_pendientes ? String(data.cuotas_pendientes) : '0');
        setPrestacionesAcumuladas(
          data.prestaciones_acumuladas ? data.prestaciones_acumuladas.toFixed(2) : '',
        );
        setCuotasDetalle(
          (data.cuotas_detalle || []).map((c, i) => ({ ...c, id: i + 1 })),
        );
        setMotivos(data.motivos || []);
        if (data.motivos?.length) setMotivoSel(data.motivos[0]);
        setPrestamoIdAnterior(data.prestamo_id || null);
        setEmpleadoCargado(true);
      } catch (e) {
        showError(e.message || 'Error al buscar empleado');
      } finally {
        setCargando(false);
      }
    },
    [post, credencialesEmpresa, showError],
  );

  const limpiar = () => {
    setEmpleadoSel(null);
    setNombres('');
    setBalanceActual('');
    setCuotasPendientes('');
    setPrestacionesAcumuladas('');
    setPrestamoSolicitado('');
    setMotivoSel(null);
    setCantidadCuotas('');
    setCuotasDetalle([]);
    setCuotasNuevas([]);
    setPrestamoIdAnterior(null);
    setEmpleadoCargado(false);
  };

  const guardar = async () => {
    if (!empleadoSel) {
      showError('No ha seleccionado ningún empleado');
      return;
    }
    if (!prestamoSolicitado || parseFloat(prestamoSolicitado) <= 0) {
      showError('El monto del préstamo solicitado no puede estar en 0');
      return;
    }
    if (!motivoSel) {
      showError('El motivo no puede estar en blanco');
      return;
    }
    const cuotas = parseInt(cantidadCuotas) || 0;
    if (cuotas <= 0) {
      showError('La cantidad de cuotas no puede estar en blanco');
      return;
    }
    setGuardando(true);
    try {
      const numemp = typeof empleadoSel === 'object' ? empleadoSel.numemp : empleadoSel;
      const { ok, data } = await post('/api/nomina_prestamo_guardar', {
        ...credencialesEmpresa(),
        numemp: String(numemp),
        monto_total: String(solicitadoMasPendiente),
        cantidad_cuotas: String(cuotas),
        motivo: motivoSel?.label || '',
        fecha,
        prestamo_id_anterior: prestamoIdAnterior || '',
      });
      if (!ok) throw new Error(data.Error || 'No se pueden grabar los datos');
      showSuccess(data.ok || 'Préstamo registrado correctamente');
      limpiar();
    } catch (e) {
      showError(e.message || 'No se pueden grabar los datos');
    } finally {
      setGuardando(false);
    }
  };

  const renderAcceso = (mensaje) => (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Registro de Préstamos"
        subtitle={nombreNegocio ? `${nombreNegocio} · Nómina` : 'Nómina'}
      />
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
        <Card
          elevation={0}
          sx={{ ...cardShellSx, maxWidth: 560, width: '100%', textAlign: 'center', p: { xs: 3, md: 4 } }}
        >
          <Box
            sx={{
              width: 64, height: 64, borderRadius: 1.6, mx: 'auto', mb: 2.5,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: FP.color.dangerSoft, color: FP.color.danger,
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 30 }} />
          </Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: FP.color.textPrimary, mb: 1 }}>
            Acceso denegado
          </Typography>
          <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
            {mensaje}
          </Typography>
          <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
            Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
          </Typography>
        </Card>
      </Box>
    </Box>
  );

  if (!puedeLeer) {
    return renderAcceso('Su rol no tiene acceso a esta opción (prestamosnomina).');
  }
  if (!puedeOperar) {
    return renderAcceso('Seleccione una empresa para administrar los préstamos.');
  }

  if (vistaEstado) {
    return <EstadoPrestamos onVolver={() => setVistaEstado(false)} />;
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Registro de Préstamos"
        subtitle={
          nombreNegocio
            ? `${nombreNegocio} · Préstamos a empleados`
            : 'Préstamos a empleados'
        }
        cta={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button startIcon={<AssessmentOutlinedIcon />} onClick={() => setVistaEstado(true)} sx={btnOutline(FP.color.navy)}>
              Estado
            </Button>
            <Button startIcon={<AddIcon />} onClick={limpiar} sx={btnOutline(FP.color.navy)}>
              Nuevo
            </Button>
          </Box>
        }
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          {/* Fecha + Empleado */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                type="date"
                label="Fecha"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Autocomplete
                options={empleados}
                value={empleadoSel}
                onChange={(_, v) => {
                  if (v) buscarEmpleado(v);
                  else limpiar();
                }}
                getOptionLabel={(o) => o?.label || ''}
                isOptionEqualToValue={(a, b) => String(a?.numemp) === String(b?.numemp)}
                disabled={empleadoCargado}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Empleado #"
                    placeholder="Buscar empleado..."
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={7}>
              <TextField
                fullWidth
                label="Nombres"
                value={nombres}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={{ ...fpInputSx, '& .MuiInputBase-root': { bgcolor: '#F8F9FA' } }}
              />
            </Grid>
          </Grid>

          {cargando ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={36} />
            </Box>
          ) : empleadoCargado ? (
            <>
              {/* Fila de datos del préstamo */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Balance Actual"
                    value={balanceActual}
                    InputProps={{ readOnly: true }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ style: { textAlign: 'right' } }}
                    sx={{ ...fpInputSx, '& .MuiInputBase-root': { bgcolor: '#F8F9FA' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Cuotas Pendientes"
                    value={cuotasPendientes}
                    InputProps={{ readOnly: true }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ style: { textAlign: 'right' } }}
                    sx={{ ...fpInputSx, '& .MuiInputBase-root': { bgcolor: '#F8F9FA' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Préstamo Solicitado"
                    value={prestamoSolicitado}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^0-9.]/g, '');
                      setPrestamoSolicitado(v);
                    }}
                    placeholder="0.00"
                    inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Autocomplete
                    options={motivos}
                    value={motivoSel}
                    onChange={(_, v) => setMotivoSel(v)}
                    getOptionLabel={(o) => o?.label || ''}
                    isOptionEqualToValue={(a, b) => a?.id === b?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Motivo"
                        InputLabelProps={{ shrink: true }}
                        sx={fpInputSx}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              {/* Fila 2: totales */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Solicitado + Bce Pendiente"
                    value={solicitadoMasPendiente ? solicitadoMasPendiente.toFixed(2) : '0.00'}
                    InputProps={{ readOnly: true }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ style: { textAlign: 'right' } }}
                    sx={{ ...fpInputSx, '& .MuiInputBase-root': { bgcolor: '#F8F9FA' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Cantidad Cuotas Nuevas"
                    value={cantidadCuotas}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '');
                      setCantidadCuotas(v);
                    }}
                    placeholder="0"
                    inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' } }}
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Prestaciones Acumuladas"
                    value={prestacionesAcumuladas}
                    InputProps={{ readOnly: true }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ style: { textAlign: 'right', color: '#D32F2F' } }}
                    sx={{ ...fpInputSx, '& .MuiInputBase-root': { bgcolor: '#F8F9FA' } }}
                  />
                </Grid>
              </Grid>

              {/* Tablas lado a lado */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <Typography
                    sx={{ fontWeight: 800, fontSize: '0.85rem', color: FP.color.textPrimary, mb: 1 }}
                  >
                    Cuotas Pendientes
                  </Typography>
                  <Box sx={{ bgcolor: '#fff' }}>
                    <DataGrid
                      autoHeight
                      rows={cuotasDetalle}
                      columns={columnsCuotasPendientes}
                      rowHeight={36}
                      pageSizeOptions={[10, 25]}
                      initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                      slots={{ noRowsOverlay: NoRowsOverlay }}
                      sx={{ ...fpDataGridSx, '--DataGrid-overlayHeight': '160px' }}
                      disableRowSelectionOnClick
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    sx={{ fontWeight: 800, fontSize: '0.85rem', color: FP.color.textPrimary, mb: 1 }}
                  >
                    Cuotas Nuevas
                  </Typography>
                  <Box sx={{ bgcolor: '#fff' }}>
                    <DataGrid
                      autoHeight
                      rows={cuotasNuevas}
                      columns={columnsCuotasNuevas}
                      rowHeight={36}
                      pageSizeOptions={[10, 25]}
                      initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                      slots={{
                        noRowsOverlay: () => (
                          <Stack
                            alignItems="center"
                            justifyContent="center"
                            sx={{ height: '100%', py: 4, color: FP.color.textMuted }}
                          >
                            <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: '0.85rem' }}>
                              Ingrese cantidad de cuotas
                            </Typography>
                          </Stack>
                        ),
                      }}
                      sx={{ ...fpDataGridSx, '--DataGrid-overlayHeight': '160px' }}
                      disableRowSelectionOnClick
                    />
                  </Box>
                </Grid>
              </Grid>

              {/* Botones */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
                <Button
                  onClick={guardar}
                  disabled={guardando}
                  startIcon={
                    guardando ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />
                  }
                  sx={btnSolid(FP.color.navy, FP.color.navyDeep)}
                >
                  {guardando ? 'Guardando…' : 'Procesar'}
                </Button>
                <Button onClick={limpiar} disabled={guardando} sx={btnOutline(FP.color.textSecondary)}>
                  Cancelar
                </Button>
                {solicitadoMasPendiente > 0 && (
                  <Typography
                    sx={{ ml: 'auto', fontWeight: 700, fontSize: '0.9rem', color: FP.color.textPrimary }}
                  >
                    Prest. Actual: {fmtMoney(solicitadoMasPendiente)}
                  </Typography>
                )}
              </Box>
            </>
          ) : (
            <Card elevation={0} sx={{ bgcolor: '#F8F9FA', borderRadius: 2, mt: 1 }}>
              <CardContent sx={{ textAlign: 'center', py: 5 }}>
                <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: '0.95rem' }}>
                  Seleccione un empleado para registrar un préstamo
                </Typography>
                <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem', mt: 0.5 }}>
                  Use el campo de búsqueda para encontrar al empleado por número o nombre.
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default RegistroPrestamos;
